import { Component, OnInit, signal, ViewChild, ViewContainerRef, inject, effect, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { Observable, catchError, concatMap, filter, map, of, Subject, take } from 'rxjs';
import { IProjectSigninConfig, ISystemMessage, IProject } from '@newroom-connect/library/interfaces';
import { AlertService, AnalyticsService, CookiesService, FileService, ProjectService, StateService, ToastService, LoggingService, SessionService, FileProcessingService, SessionChangeEvent, AreaService, ChatWidgetService } from '@newroom-connect/library/services';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';
import { ModalService } from '@newroom-connect/library/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArrayHelper } from '@newroom-connect/library/helpers';

import { environment } from '../../../../environments/environment';
import packageInfo from '../../package.json';

import { NavigationService } from './services/navigation.service';

@Component({
  selector: 'nrc-web-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fadeInOutAnimation]
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly PUBLIC_URL_PATHS = [
    '/signin',
    '/signup',
    '/404'
  ];

  @ViewChild('modalContainer', { read: ViewContainerRef, static: true }) private modalContainer!: ViewContainerRef;

  public title = 'Newroom Connect Virtual Studio';
  public appVersion = packageInfo['version'];
  public isProdEnvironment = environment.production;

  public isLoading$: Observable<boolean>;
  public alertMessage = signal<ISystemMessage | null>(null).asReadonly();
  public toastMessage = signal<ISystemMessage | null>(null).asReadonly();
  public currentSignInConfig = signal<IProjectSigninConfig | null>(null).asReadonly();
  public showLogo = signal<boolean>(false);

  public currentProjectSlug = signal<string | null>(null);
  public currentProject = signal<IProject | null>(null);

  private cookiesInitialized = false;
  private currentProjectSignInConfig?: IProjectSigninConfig;
  private destroy$ = new Subject<void>();

  public readonly fileService = inject(FileService);
  private readonly logger = inject(LoggingService);
  private readonly sessionService = inject(SessionService);
  private readonly chatWidgetService = inject(ChatWidgetService);

  /**
   * @constructor
   *
   * @param router
   * @param route
   * @param alertService
   * @param areaService
   * @param toastService
   * @param fileProcessingService
   * @param stateService
   * @param projectService
   * @param navigationService
   * @param analyticsService
   * @param cookiesService
   * @param modalService
   */
  constructor(
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    public readonly alertService: AlertService,
    public readonly areaService: AreaService,
    public readonly toastService: ToastService,
    public readonly fileProcessingService: FileProcessingService,
    private readonly stateService: StateService,
    private readonly projectService: ProjectService,
    private readonly navigationService: NavigationService,
    private readonly analyticsService: AnalyticsService,
    private readonly cookiesService: CookiesService,
    private readonly modalService: ModalService
  ) {
    this.isLoading$ = this.stateService.watchLoadingState().pipe(takeUntilDestroyed());

    this.sessionService.watchSessionChanges().pipe(
      concatMap((event: SessionChangeEvent) => this.clearCachedDataOnLogout(event)),
      takeUntilDestroyed()
    ).subscribe((event: SessionChangeEvent) => {
      this.navigationService.handleSessionChange(event);

      // If session changes (e.g. session has been refreshed or new session has been created) dispatch auth changed event for chat widget
      this.chatWidgetService.handleSessionRefresh(event?.userSession?.getAccessToken() ?? null);
    });

    // Listen for project slug in route params and initialize analytics.
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event) => {
      this.showLogo.set(AppComponent.PUBLIC_URL_PATHS.some(url => event.url.startsWith(url)));

      // Scroll to top on navigation
      window.scrollTo(0, 0);

      // Get the first child route which should have the projectSlug parameter
      const childRoute = this.route.firstChild;

      if (!childRoute) {
        return;
      }

      childRoute.params.pipe(take(1)).subscribe(params => {
        const projectSlug = params['projectSlug'];

        if (!projectSlug || this.currentProjectSlug() === projectSlug) {
          return;
        }

        this.currentProjectSlug.set(projectSlug);
      });
    });

    // Load project config and project data when slug changes.
    effect(() => {
      const slug = this.currentProjectSlug();

      if (!slug) {
        this.currentProject.set(null);
        return;
      }

      // Load project signin config and initialize analytics if not already done
      if (!this.cookiesInitialized && !this.currentProjectSignInConfig) {
        this.projectService.getProjectSigninConfig(slug).pipe(take(1)).subscribe(signInConfig => {
          this.currentProjectSignInConfig = signInConfig;

          this.initializeUsercentrics(signInConfig);
          this.initializeGoogleAnalytics(signInConfig);
        });
      }

      // Load project data and initialize chat widget
      this.projectService.getProjectBySlug(slug).pipe(take(1)).subscribe({
        next: (project) => {
          this.currentProject.set(project);

          // Initialize chat widget if enabled
          if (project.chatbotConfig) {
            this.chatWidgetService.initialize(environment.chatWidget.cdnUrl);
          } else {
            this.chatWidgetService.cleanup();
          }
        },
        error: (error) => {
          this.logger.error('Failed to load project', error);
          this.currentProject.set(null);
        }
      });
    }, { allowSignalWrites: true });
  }

  /**
   * Cleanup on component destruction.
   */
  public ngOnDestroy(): void {
    this.chatWidgetService.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   *
   */
  public async ngOnInit(): Promise<void> {
    this.alertMessage = this.alertService.watchMessage();
    this.toastMessage = this.toastService.watchMessage();
    this.currentSignInConfig = this.projectService.watchCurrentSignInConfig();

    this.modalService.setRootViewContainerRef(this.modalContainer);
  }

  /**
   *
   * @param signInConfig
   * @returns
   */
  private async initializeUsercentrics(signInConfig: IProjectSigninConfig): Promise<void> {
    if (!signInConfig || !ArrayHelper.isNotEmpty(signInConfig.analytics?.providers)) {
      return;
    }

    const usercentricsProvider = signInConfig.analytics.providers.find(provider =>
      provider.preset.id === CookiesService.USERCENTRICS_PRESET_ID && provider.enabled
    );

    if (!usercentricsProvider) {
      return;
    }

    this.cookiesService.initialize(usercentricsProvider.analyticsKeyId);
  }

  /**
   *
   * @param signInConfig
   * @returns
   */
  private async initializeGoogleAnalytics(signInConfig: IProjectSigninConfig): Promise<void> {
    if (!signInConfig || !ArrayHelper.isNotEmpty(signInConfig.analytics?.providers)) {
      return;
    }

    const googleAnalyticsProvider = signInConfig.analytics.providers.find(provider =>
      provider.preset.id === AnalyticsService.GOOGLE_ANALYTICS_PRESET_ID && provider.enabled
    );

    if (!googleAnalyticsProvider || !googleAnalyticsProvider.analyticsKeyId || !googleAnalyticsProvider.enabled) {
      return;
    }

    this.analyticsService.setAnalyticsSettings(environment.config.googleAnalytics.settings, googleAnalyticsProvider.analyticsKeyId, true);
  }

  /**
   * Clear session-owned caches before logout navigation runs.
   *
   * @param event
   *
   * @returns Session change event after logout cleanup has completed.
   */
  private clearCachedDataOnLogout(event: SessionChangeEvent): Observable<SessionChangeEvent> {
    if (event.userSession) {
      return of(event);
    }

    this.chatWidgetService.cleanup();

    return this.projectService.clearCachedData().pipe(
      take(1),
      map(() => event),
      // Keep catchError last so the fallback emits the same SessionChangeEvent type as this helper.
      catchError(error => {
        this.logger.error('Error clearing cached data on logout:', error);

        return of(event);
      })
    );
  }
}
